import type { ExportPreferences } from '@gfe/core/store/user-preferences/user-preferences.model';

/** An external-reference column: its raw CSV field key and the referenced entity type. */
export interface ReferenceColumn {
  field: string;
  entityType: string;
}

/**
 * The CSV column key that will hold a reference's resolved name. A trailing
 * `_uid` is swapped for `_name` (`site_uid` -> `site_name`); otherwise `_name`
 * is appended (`pickup_site` -> `pickup_site_name`). `taken` guards against
 * clobbering an existing column: the swap is used only when its result is free,
 * else `_name` is appended to the full field, with `_reference_name` as a last
 * resort.
 */
export function referenceNameColumnKey(field: string, taken: Set<string>): string {
  const swapped = field.endsWith('_uid') ? `${field.slice(0, -4)}_name` : `${field}_name`;
  if (!taken.has(swapped)) return swapped;
  const appended = `${field}_name`;
  if (!taken.has(appended)) return appended;
  return `${field}_reference_name`;
}

/**
 * Resolves a stable name-column key for each reference field, threading a shared
 * `taken` set so two references never collide on the same generated key.
 */
export function referenceNameColumnKeys(
  refCols: ReferenceColumn[],
  existingColumns: Iterable<string>
): Map<string, string> {
  const taken = new Set<string>(existingColumns);
  const keys = new Map<string, string>();
  for (const { field } of refCols) {
    const key = referenceNameColumnKey(field, taken);
    keys.set(field, key);
    taken.add(key);
  }
  return keys;
}

/**
 * Collects the referenced uids present on `rows`, grouped by target entity type
 * and de-duplicated. Array (uid-list) values are flattened; empty / non-string
 * entries are ignored. Used to drive the batched name-resolution requests.
 */
export function collectReferenceUidsByType(
  refCols: ReferenceColumn[],
  rows: Array<Record<string, unknown>>
): Map<string, string[]> {
  const byType = new Map<string, Set<string>>();

  for (const { field, entityType } of refCols) {
    for (const row of rows) {
      const value = row[field];
      const uids = Array.isArray(value) ? value : value != null ? [value] : [];
      for (const uid of uids) {
        if (typeof uid !== 'string' || uid === '') continue;
        if (!byType.has(entityType)) byType.set(entityType, new Set());
        byType.get(entityType)!.add(uid);
      }
    }
  }

  return new Map([...byType].map(([type, uids]) => [type, [...uids]]));
}

function nameForUid(uid: unknown, names: Map<string, string> | undefined): string {
  if (typeof uid !== 'string' || uid === '') return '';
  return names?.get(uid) ?? '';
}

function nameForValue(value: unknown, names: Map<string, string> | undefined): string {
  if (Array.isArray(value)) {
    return value
      .map((uid) => nameForUid(uid, names))
      .filter((name) => name !== '')
      .join(',');
  }
  return nameForUid(value, names);
}

/**
 * Adds a resolved-name column to each row for every reference field. A scalar
 * uid becomes its resolved name; a uid-list becomes the comma-joined names;
 * anything unresolved becomes an empty string. Input rows are left untouched.
 */
export function augmentRowsWithReferenceNames<T extends Record<string, unknown>>(
  rows: T[],
  refCols: ReferenceColumn[],
  namesByType: Map<string, Map<string, string>>,
  nameKeyByField: Map<string, string>
): T[] {
  if (refCols.length === 0) return rows;
  return rows.map((row) => {
    const augmented: Record<string, unknown> = { ...row };
    for (const { field, entityType } of refCols) {
      const nameKey = nameKeyByField.get(field);
      if (!nameKey) continue;
      augmented[nameKey] = nameForValue(row[field], namesByType.get(entityType));
    }
    return augmented as T;
  });
}

/**
 * Applies the export preferences to an ordered column list: a reference's uid
 * column is dropped when `includeUid` is off, and its name column is inserted
 * immediately after when `includeName` is on. The entity's own columns and any
 * non-reference column are always kept. Returns the input array unchanged (same
 * reference) for the default case (uid on, name off).
 */
export function buildReferenceExportColumns(
  baseColumns: string[],
  refCols: ReferenceColumn[],
  prefs: ExportPreferences,
  nameKeyByField: Map<string, string>
): string[] {
  const includeUid = prefs.includeUid !== false;
  const includeName = prefs.includeName === true;
  if (includeUid && !includeName) return baseColumns;

  const refFields = new Set(refCols.map((ref) => ref.field));
  const result: string[] = [];
  for (const col of baseColumns) {
    if (!refFields.has(col)) {
      result.push(col);
      continue;
    }
    if (includeUid) result.push(col);
    if (includeName) {
      const nameKey = nameKeyByField.get(col);
      if (nameKey) result.push(nameKey);
    }
  }
  return result;
}
